import React, { useState } from "react";
import { Users, ChevronDown, ChevronUp, Clock, X, RefreshCw } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import {
  useWaitingRoomByDoctor,
  useRemovePatientFromWaitingRoom,
  useWaitingRoomRealtimeSync,
} from "../queries";
import { QueryBackgroundSync, QueryStaleIndicator } from "./query";

const WaitingRoom = ({ onRemovePatient, roomId: propRoomId }) => {
  const { userData } = useUserContext();
  const [waitingRoomExpanded, setWaitingRoomExpanded] = useState(false);

  const doctorId =
    userData?.users?.type === "Nurse"
      ? userData?.users?.parent_id
      : userData?.users?.id;

  // TanStack Query server-state hook
  const waitingRoomQuery = useWaitingRoomByDoctor(doctorId);
  const removePatientMutation = useRemovePatientFromWaitingRoom();

  const roomData = waitingRoomQuery.data;
  const roomId = propRoomId || roomData?.id;
  const waitingPatients = roomData?.patients || [];

  // Real-time: refresh when a patient enters/leaves the room (e.g. the doctor
  // finishes an appointment from the consultation), no reload needed.
  useWaitingRoomRealtimeSync(doctorId);

  const handleRemovePatient = (patientId) => {
    if (!roomId) return;

    removePatientMutation.mutate({
      roomId,
      doctorId,
      patient_id: patientId,
    });

    onRemovePatient?.(patientId);
  };

  const handleRefreshPatients = () => {
    waitingRoomQuery.refetch();
  };

  return (
    <li className="expandable-section waiting-room-section">
      <div
        className="expandable-header"
        onClick={() => setWaitingRoomExpanded(!waitingRoomExpanded)}
      >
        <div className="expandable-header-content">
          <div className="iconDiv">
            <Users size={20} />
          </div>
          <span>Waiting Room</span>
          {waitingPatients.length > 0 && (
            <span className="badge-count">{waitingPatients.length}</span>
          )}
        </div>

        <div
          className="chevron-icon"
          style={{ display: "flex", gap: "6px", alignItems: "center" }}
        >
          <QueryBackgroundSync
            isFetching={waitingRoomQuery.isFetching}
            isLoading={waitingRoomQuery.isLoading}
            variant="compact"
          />

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRefreshPatients();
            }}
            disabled={waitingRoomQuery.isFetching}
            title="Refresh waiting room"
            style={{
              background: "none",
              border: "none",
              cursor: waitingRoomQuery.isFetching ? "not-allowed" : "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              opacity: waitingRoomQuery.isFetching ? 0.5 : 1,
            }}
          >
            <RefreshCw
              size={15}
              className={waitingRoomQuery.isFetching ? "rotating animate-spin" : ""}
            />
          </button>
          {waitingRoomExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {waitingRoomExpanded && (
        <ul className="waiting-room-list">
          {waitingRoomQuery.isLoading ? (
            <li className="empty-state" style={{ color: "#64748b" }}>
              Loading waiting room...
            </li>
          ) : waitingRoomQuery.isError ? (
            <li className="empty-state" style={{ color: "#dc2626" }}>
              Unable to load waiting room
            </li>
          ) : waitingPatients.length === 0 ? (
            <li className="empty-state">No patients waiting</li>
          ) : (
            waitingPatients.map((patient) => (
              <li key={patient.id} className={`patient-item ${patient.status || "waiting"}`}>
                <div className="patient-info">
                  <div className="patient-name">{patient.name}</div>
                  <div className="patient-time">
                    <Clock size={12} />
                    <span>{patient.time}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemovePatient(patient.id);
                  }}
                  title="Remove patient"
                  disabled={removePatientMutation.isPending}
                >
                  <X size={14} />
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </li>
  );
};

export default WaitingRoom;
